import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, FetchAPI, FieldAPI, FormAPI, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import { AbortErrorInterface } from '../../../interfaces/double-conference/AbortErro';
import ShowToast from '../../../utils/toast-service';
import BaseController from '../../BaseController';
import { InfusionsViewerParams } from '../infusion-viewer/infusions-viewer-params';

import './infusion-suspend-with-justification-controller.css'

interface SuspendInfusionResponse {
  errorStepNumber: string;
}

interface FormSuspendJustificationRecord {
  justification: string;
  reasonType: {
    dsReason: string;
    sequenceId: number;
  },
  stepType: {
    id: number;
    stepDescription: string;
  }
}

@Controller({ component: 'layout-infusion-suspend-with-justification', fragment: 'infusion-suspend-with-justification' })
class InfusionSuspendWithJustificationController extends BaseController {

  @Inject('form-infusion-suspend')
  form!: FormAPI;

  @Inject('field-reason')
  fieldReason!: FieldAPI;

  @Inject('field-text-area-justification')
  fieldJustification!: FieldAPI;

  @Inject('autocomplete-infusion-suspend-step')
  autocompleteStep!: AutocompleteAPI;

  @Inject('autocomplete-infusion-suspend-reason')
  autocompleteReason!: AutocompleteAPI;

  @Inject('fetch-infusion-suspend-step')
  fetchSteps!: FetchAPI;

  @Inject('panel-loader')
  feedbackPanelLoader!: PanelAPI;

  @OnMount({target: 'layout-infusion-suspend-with-justification'})
  onLayoutMount(): void {
    const {
      patientId,
      encounterId,
      prescriptionNumber,
      sequenceNumber
    } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId,
      patientId,
      prescriptionNumber,
      sequenceNumber,
      solutionSequenceNumber: InfusionsViewerParams.adminSchedule.solutionSequenceNumber
    });
  }

  @OnBeforeSubmit({ target: 'form-infusion-suspend' })
  async onBeforeSubmitForm(): Promise<void> {
    const isValid = await this.form.validate();
    if (!isValid) {
      return;
    }
    this.toggleFormLoading();

    const body = this.createBody();

    HttpService.post<SuspendInfusionResponse>(Endpoint.SUSPEND_PRESCRIPTION_STEP, {}, { body })
      .then(this.handleSuspendPrescriptionResponse)
      .catch(error => this.handleSuspendError(error))
  }

  handleSuspendPrescriptionResponse = (response: HttpResponse<SuspendInfusionResponse>): void => {
    const {encounterId, patientId} = this.historyService.getCurrentRouteParams();
    this.toggleFormLoading();
    if(!response.body?.errorStepNumber) {
      this.openSuccessToast();
    } else {
      this.openErrorToast(response.body.errorStepNumber);
    }

    applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
      encounterId,
      patientId,
    });
    applicationContext.clearNavigationStack();
  }

  createBody(): Record<string, Value> {
    const formRecord = this.form.getRecord() as unknown as FormSuspendJustificationRecord;

    return {
      eventStepNumber: formRecord.stepType?.id,
      justificationDescription: formRecord.justification,
      prescriptionNumber: this.getHistoryRouteParam<string>('prescriptionNumber'),
      reasonSequenceNumber: formRecord.reasonType?.sequenceId,
      solutionSequence: InfusionsViewerParams.adminSchedule.solutionSequenceNumber,
      suspendStepNumber: String(formRecord.stepType?.id),
    }
  }

  handleSuspendError(response: AbortErrorInterface) : void {
    this.toggleFormLoading()
    if (response.body?.errors) {
      response.body.errors.forEach(error => ShowToast.open(error, ToastLevel.ERROR));
    }
  }

  toggleFormLoading(): void {
    this.form.setLoading(!this.form.isLoading());
    this.form.setDisabled(!this.form.isDisabled());
  }

  openErrorToast(errorStepNumber: string): void {
    const message = MessageService.translate('prescription-step.suspend-step-fail');

    ToastService.open({
      dismissible: true,
      id: 'prescription-step.suspend-step-fail',
      level: ToastLevel.ERROR,
      message: message.replace('#@NR_ETAPAS_ERRO#@', errorStepNumber),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  openSuccessToast(): void {
    ToastService.open({
      dismissible: true,
      id: 'prescription-step.suspend-step-success',
      level: ToastLevel.SUCCESS,
      message: 'prescription-step.suspend-step-success',
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  @OnBeforeCancel({ target: 'form-infusion-suspend' })
  clearForm(event: AbortableCloseEvent): void {
    event.abort();
    applicationContext.popNavigationItem();
  }
}

export default InfusionSuspendWithJustificationController;
